import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Layout from "../layout/index";
import { ROOT_API } from "../../api/config";
import "./style.scss";

// Javna stranica cjenika — tekst je namjerno samo na hrvatskom (zakonska objava),
// bez i18n kljuceva, isto kao /uvjeti i /privatnost.

// Datumi se formatiraju u hrvatskoj vremenskoj zoni jer je rok objave (08:00)
// propisan lokalnim vremenom, a posjetitelj moze biti u drugoj zoni.
const TZ = "Europe/Zagreb";

// "2026-06-17T08:00:00+02:00" -> "17.06.2026. 08:00"
const formatDateTimeHr = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("hr-HR", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(d)
    .reduce((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
  if (!parts.year || !parts.hour) return String(value);
  return `${parts.day}.${parts.month}.${parts.year}. ${parts.hour}:${parts.minute}`;
};

// "2026-09-10" -> "10.09.2026."
const formatDateHr = (value) => {
  if (!value) return "";
  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}.`;
  return String(value);
};

const price = (value) => {
  const raw = (value === 0 || value ? String(value) : "").trim();
  return raw ? `${raw} €` : "—";
};

const Cjenik = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setHasError(false);
    axios
      .get(`${ROOT_API}apis/cjenik.json`, { timeout: 8000 })
      .then((res) => {
        if (!alive) return;
        setData(res?.data || null);
        setIsLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setHasError(true);
        setIsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  const pruzatelj = data?.pruzatelj || {};
  const datoteke = data?.datoteke || {};
  const usluge = Array.isArray(data?.usluge) ? data.usluge : [];
  const arhiva = Array.isArray(data?.arhiva) ? data.arhiva : [];
  // Arhiva se prikazuje od najnovije pohrane prema starijima.
  const arhivaSorted = [...arhiva].sort(
    (a, b) => Number(b?.broj_pohrane || 0) - Number(a?.broj_pohrane || 0)
  );
  const sidrenaNaDan = formatDateHr(data?.sidrena_cijena_na_dan) || "10.09.2026.";

  return (
    <Layout>
      <div className="container">
        <div className="cjenik py-36">
          <h1 className="cjenik__title font-28 font-bold mb-8">Cjenik usluga</h1>

          <p className="cjenik__lead">
            Sve cijene izražene su u eurima i uključuju PDV (maloprodajne, bruto
            cijene). Cjenik je objavljen u skladu s Odlukom o objavi cjenika
            proizvoda i usluga kao mjera izravne kontrole cijena (NN 101/2026).
          </p>
          <p className="cjenik__lead">
            Objavljene su redovne maloprodajne cijene. Osobni promo i preporuka
            kodovi nisu poseban oblik prodaje pa ne mijenjaju objavljenu cijenu;
            kada se usluga prodaje u posebnom obliku prodaje (npr. akcija), to je
            navedeno u stupcu „Posebni oblik prodaje”. Sidrena cijena se, prema
            Odluci, uvijek iskazuje bez posebnih oblika prodaje.
          </p>
          <p className="cjenik__lead">
            Sidrena cijena je dodatna cijena koja se ističe uz maloprodajnu
            cijenu i predstavlja cijenu usluge koja se primjenjivala na dan{" "}
            10. rujna 2026., u skladu s Odlukom o isticanju dodatne cijene kao
            mjera izravne kontrole cijena (NN 101/2026).
          </p>
          <p className="cjenik__lead">
            Svaka izmjena cjenika objavljuje se na ovoj stranici do 08:00 sati
            onog dana od kojeg se primjenjuje, a svaka objavljena verzija
            cjenika ostaje dostupna 30 dana od objave.
          </p>

          {isLoading && (
            <div className="cjenik__state" aria-busy="true">
              <p className="cjenik__muted">Učitavanje cjenika…</p>
              <div className="cjenik__skeleton" />
              <div className="cjenik__skeleton" />
              <div className="cjenik__skeleton cjenik__skeleton--short" />
            </div>
          )}

          {!isLoading && hasError && (
            <div className="cjenik__error">
              <p>
                Cjenik trenutačno nije moguće učitati. Provjeri internetsku
                vezu i pokušaj ponovno.
              </p>
              <button
                type="button"
                className="btn-cta cjenik__retry"
                onClick={retry}
              >
                Pokušaj ponovno
              </button>
            </div>
          )}

          {!isLoading && !hasError && (
            <>
              <section className="cjenik__section">
                <h2 className="cjenik__section-title">Pružatelj usluge</h2>
                <div className="cjenik__card">
                  <dl className="cjenik__meta">
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">Naziv</dt>
                      <dd className="cjenik__meta-value">
                        {pruzatelj?.naziv || ""}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">OIB</dt>
                      <dd className="cjenik__meta-value">
                        {pruzatelj?.oib || ""}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">Adresa</dt>
                      <dd className="cjenik__meta-value">
                        {pruzatelj?.adresa || ""}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">Oznaka objekta</dt>
                      <dd className="cjenik__meta-value">
                        {pruzatelj?.oznaka_objekta || ""}
                        {pruzatelj?.oblik_objekta
                          ? ` (${pruzatelj.oblik_objekta})`
                          : ""}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">Mrežna stranica</dt>
                      <dd className="cjenik__meta-value">
                        {pruzatelj?.mrezna_stranica ? (
                          <a
                            href={pruzatelj.mrezna_stranica}
                            rel="noreferrer noopener"
                          >
                            {pruzatelj.mrezna_stranica}
                          </a>
                        ) : (
                          ""
                        )}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">Broj pohrane</dt>
                      <dd className="cjenik__meta-value">
                        {data?.broj_pohrane === 0 || data?.broj_pohrane
                          ? String(data.broj_pohrane)
                          : ""}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">Objavljeno</dt>
                      <dd className="cjenik__meta-value">
                        {formatDateTimeHr(data?.objavljeno)}
                      </dd>
                    </div>
                    <div className="cjenik__meta-row">
                      <dt className="cjenik__meta-label">
                        Sidrena cijena na dan
                      </dt>
                      <dd className="cjenik__meta-value">{sidrenaNaDan}</dd>
                    </div>
                  </dl>
                </div>
              </section>

              <section className="cjenik__section">
                <h2 className="cjenik__section-title">Usluge i cijene</h2>
                <div className="cjenik__table-wrap">
                  <table className="cjenik__table">
                    <thead>
                      <tr>
                        <th>Usluga</th>
                        <th>Jedinica</th>
                        <th>Aktualna cijena</th>
                        <th>Sidrena cijena (10.09.2026.)</th>
                        <th>Posebni oblik prodaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usluge.length === 0 && (
                        <tr>
                          <td className="cjenik__empty" colSpan={5}>
                            Trenutačno nema objavljenih usluga u cjeniku.
                          </td>
                        </tr>
                      )}
                      {usluge.map((u, i) => {
                        const poseban =
                          String(u?.posebni_oblik_prodaje || "NE").toUpperCase() !==
                          "NE";
                        return (
                          <tr key={u?.sifra || `usluga-${i}`}>
                            <td data-label="Usluga">
                              <span className="cjenik__usluga">
                                {u?.naziv_usluge || ""}
                              </span>
                              {u?.sifra ? (
                                <span className="cjenik__sifra">{u.sifra}</span>
                              ) : null}
                            </td>
                            <td data-label="Jedinica">
                              {u?.jedinica_mjere || ""}
                            </td>
                            <td data-label="Aktualna cijena">
                              <span className="cjenik__price">
                                {price(u?.maloprodajna_cijena)}
                              </span>
                            </td>
                            <td data-label="Sidrena cijena (10.09.2026.)">
                              <span className="cjenik__price cjenik__price--anchor">
                                {price(u?.sidrena_cijena)}
                              </span>
                            </td>
                            <td data-label="Posebni oblik prodaje">
                              {poseban
                                ? `DA — ${u?.naziv_posebnog_oblika_prodaje || ""}`.trim()
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {data?.pravni_izvor ? (
                  <p className="cjenik__muted cjenik__legal">
                    Pravni izvor: {data.pravni_izvor}
                  </p>
                ) : null}
              </section>

              {(datoteke?.csv || datoteke?.xml) && (
                <section className="cjenik__section">
                  <h2 className="cjenik__section-title">Preuzimanje cjenika</h2>
                  <p className="cjenik__muted">
                    Cjenik je objavljen i u strojno čitljivom obliku (.csv i
                    .xml) pa ga je moguće preuzeti i automatizirano, bez
                    prijave.
                  </p>
                  {/* The prescribed file name (NN 101/2026 VI.) is shown as a caption BELOW the
                      button — inside it the global .btn-cta layout keeps it on one line. */}
                  <div className="cjenik__files">
                    {datoteke?.csv ? (
                      <div className="cjenik__file-item">
                        <a
                          className="btn-cta cjenik__file"
                          href={datoteke.csv}
                          download={datoteke?.naziv_csv || ""}
                        >
                          Preuzmi .csv
                        </a>
                        {datoteke?.naziv_csv ? (
                          <span className="cjenik__file-name">
                            {datoteke.naziv_csv}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {datoteke?.xml ? (
                      <div className="cjenik__file-item">
                        <a
                          className="btn-cta cjenik__file"
                          href={datoteke.xml}
                          download={datoteke?.naziv_xml || ""}
                        >
                          Preuzmi .xml
                        </a>
                        {datoteke?.naziv_xml ? (
                          <span className="cjenik__file-name">
                            {datoteke.naziv_xml}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </section>
              )}

              {arhivaSorted.length > 0 && (
                <section className="cjenik__section">
                  <h2 className="cjenik__section-title">
                    Arhiva objavljenih cjenika
                  </h2>
                  <ul className="cjenik__archive">
                    {arhivaSorted.map((a, i) => (
                      <li
                        className="cjenik__archive-item"
                        key={`${a?.broj_pohrane || "pohrana"}-${i}`}
                      >
                        <span className="cjenik__archive-label">
                          Broj pohrane{" "}
                          {a?.broj_pohrane === 0 || a?.broj_pohrane
                            ? String(a.broj_pohrane)
                            : ""}
                          {a?.objavljeno
                            ? ` · objavljeno ${formatDateTimeHr(a.objavljeno)}`
                            : ""}
                          {a?.aktualno ? (
                            <span className="cjenik__badge">aktualno</span>
                          ) : null}
                        </span>
                        <span className="cjenik__archive-links">
                          {a?.csv ? (
                            <a href={a.csv} download>
                              CSV
                            </a>
                          ) : null}
                          {a?.xml ? (
                            <a href={a.xml} download>
                              XML
                            </a>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {data?.generirano ? (
                <p className="cjenik__muted cjenik__generated">
                  Podaci na ovoj stranici generirani su{" "}
                  {formatDateTimeHr(data.generirano)}.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Cjenik;
